/**
 * HotspotImage - Frontend interaction handler.
 *
 * Manages hover, click, touch interactions for hotspot image blocks.
 * Handles polygon highlighting, marker interactions, tooltips,
 * inverse highlight overlays, and mobile touch support.
 */

// Import CSS for Vite bundling.
import '../css/blocks/block-hotspot-image.css';

class HotspotImage {
	/**
	 * @param {HTMLElement} container - The block container element.
	 */
	constructor(container) {
		this.container = container;
		this.settings = this.parseSettings();
		this.activePolygonId = null;
		this.isTouchDevice = false;
		this.tooltipVisible = false;

		// The polygon that is permanently "selected" by default (set in editor).
		this.defaultPolygonId = this.settings.defaultPolygon || null;

		// Per-polygon deactivation timers. Any related element (polygon, marker,
		// tooltip) cancels the timer on mouseenter so fast transitions between
		// related elements don't cause flickering.
		this._deactivateTimers = {};

		// Cache DOM references.
		this.svg = container.querySelector('.wp-block-hotspot-image__svg');
		// Static polygons/markers have no link and no tooltip content to reveal.
		this.polygons = container.querySelectorAll('.wp-block-hotspot-image__polygon:not(.is-static)');
		this.markers = container.querySelectorAll('.wp-block-hotspot-image__marker:not(.is-static)');
		this.tooltips = container.querySelectorAll('.wp-block-hotspot-image__tooltip');
		this.inverseOverlays = container.querySelectorAll('.wp-block-hotspot-image__inverse-overlay');

		this.init();
	}

	/**
	 * Parse block settings from data attributes.
	 */
	parseSettings() {
		return {
			displayMode: this.container.dataset.displayMode || 'both',
			desktopTrigger: this.container.dataset.desktopTrigger || 'hover',
			mobileTrigger: this.container.dataset.mobileTrigger || 'tap_reveal',
			inverseHighlight: this.container.dataset.inverseHighlight === 'true',
			contentDisplay: this.container.dataset.contentDisplay || 'tooltip',
			showMarkerLabels: this.container.dataset.showMarkerLabels === 'true',
			// Default polygon: always shown as active. ID stored in polygon JSON via editor.
			defaultPolygon: this.container.dataset.defaultPolygon || '',
			// Allow users to hover/click other polygons when a default is set.
			// After interaction ends the view always reverts to the default polygon.
			allowInteraction: this.container.dataset.allowInteraction !== 'false',
		};
	}

	/**
	 * Initialize all event handlers.
	 */
	init() {
		// Detect touch capability.
		this.detectTouch();

		// Disable pointer-events on non-default polygons when interaction is off.
		this.applyInteractionMode();

		// Bind polygon interactions.
		this.bindPolygonEvents();

		// Bind marker interactions.
		this.bindMarkerEvents();

		// Bind tooltip close buttons.
		this.bindTooltipCloseEvents();

		// Bind click-outside to dismiss.
		this.bindOutsideClickEvent();

		// Bind keyboard events.
		this.bindKeyboardEvents();

		// Bind resize handler for tooltip repositioning.
		this.bindResizeHandler();

		// When a default is configured, mark the container so CSS can hide
		// non-active markers automatically.
		if (this.defaultPolygonId) {
			this.container.classList.add('has-default-polygon');
		}

		// Activate the default polygon on page load (if one is configured).
		if (this.defaultPolygonId) {
			this.activatePolygon(this.defaultPolygonId);
		}
	}

	/**
	 * Disable pointer-events on polygons/markers that should not respond to
	 * hover or click:
	 *   - The default polygon is static (it's permanently active) unless it
	 *     has a link, in which case clicking it follows the link.
	 *   - Non-default polygons are also disabled when allowInteraction is false.
	 */
	applyInteractionMode() {
		this.polygons.forEach((polygon) => {
			if (this.hasPolygonLink(polygon.dataset.polygonId)) {
				polygon.style.cursor = 'pointer';
			}
		});

		if (!this.defaultPolygonId) return;

		this.polygons.forEach((polygon) => {
			if (!this.isPolygonClickable(polygon.dataset.polygonId)) {
				polygon.style.pointerEvents = 'none';
			}
		});

		this.markers.forEach((marker) => {
			if (!this.isPolygonClickable(marker.dataset.polygonId)) {
				marker.style.pointerEvents = 'none';
				marker.setAttribute('tabindex', '-1');
				marker.setAttribute('aria-hidden', 'true');
			}
		});
	}

	/**
	 * Whether a polygon's tooltip contains a link.
	 */
	hasPolygonLink(polygonId) {
		return !!this.container.querySelector(
			`.wp-block-hotspot-image__tooltip[data-polygon-id="${polygonId}"] .wp-block-hotspot-image__tooltip-link[href]`
		);
	}

	/**
	 * Whether a click on a polygon (shape or marker) should do anything.
	 */
	isPolygonClickable(polygonId) {
		if (polygonId === this.defaultPolygonId) {
			return this.hasPolygonLink(polygonId);
		}
		return this.settings.allowInteraction;
	}

	/**
	 * Deactivate a polygon and then restore the default selection if one is
	 * configured and the dismissed polygon was not the default itself.
	 * Use this instead of calling deactivatePolygon() directly whenever a
	 * user-initiated dismissal occurs (hover-out timer, close button, outside
	 * click, Escape key).
	 */
	deactivateAndRestoreDefault(polygonId) {
		this.deactivatePolygon(polygonId);
		// Restore only when the dismissed polygon was NOT the default (clicking
		// close on the default tooltip intentionally hides it).
		if (this.defaultPolygonId && this.activePolygonId === null && polygonId !== this.defaultPolygonId) {
			this.activatePolygon(this.defaultPolygonId);
		}
	}

	/**
	 * Detect if this is a touch device.
	 */
	detectTouch() {
		// Listen for first touch event to switch modes.
		const onFirstTouch = () => {
			this.isTouchDevice = true;
			this.container.classList.add('is-touch-device');
			window.removeEventListener('touchstart', onFirstTouch);
		};
		window.addEventListener('touchstart', onFirstTouch, { passive: true });

		// Also check media query.
		if (window.matchMedia('(hover: none)').matches) {
			this.isTouchDevice = true;
			this.container.classList.add('is-touch-device');
		}
	}

	/**
	 * Cancel a pending deactivation timer for a polygon.
	 */
	cancelDeactivation(polygonId) {
		if (this._deactivateTimers[polygonId]) {
			clearTimeout(this._deactivateTimers[polygonId]);
			delete this._deactivateTimers[polygonId];
		}
	}

	/**
	 * Schedule a deferred deactivation for a polygon. The timer is cancelled
	 * if any related element (polygon, marker, tooltip) receives a mouseenter
	 * before the delay expires.
	 * After the timer fires the default polygon (if any) is restored.
	 */
	scheduleDeactivation(polygonId, delay = 60) {
		this.cancelDeactivation(polygonId);
		this._deactivateTimers[polygonId] = setTimeout(() => {
			delete this._deactivateTimers[polygonId];
			this.deactivateAndRestoreDefault(polygonId);
		}, delay);
	}

	/**
	 * Check whether a mouseleave event's relatedTarget belongs to another
	 * element associated with the same polygon (marker, polygon SVG, tooltip,
	 * or a child of any of those). If so, the cursor is still "inside" the
	 * polygon's interactive area and we should NOT schedule deactivation.
	 */
	isRelatedToSamePolygon(event, polygonId) {
		const related = event.relatedTarget;
		if (!related) return false;

		const selectors = [
			`.wp-block-hotspot-image__polygon[data-polygon-id="${polygonId}"]`,
			`.wp-block-hotspot-image__marker[data-polygon-id="${polygonId}"]`,
			`.wp-block-hotspot-image__tooltip[data-polygon-id="${polygonId}"]`,
		];

		for (const sel of selectors) {
			// Direct match (works for both SVG and HTML elements).
			try { if (related.matches?.(sel)) return true; } catch (e) {}
			// Child of a matching element (e.g. icon inside marker).
			try { if (related.closest?.(sel)) return true; } catch (e) {}
		}
		return false;
	}

	/**
	 * Bind events to SVG polygon elements.
	 * Shapes without a link are hover-only so taps on the large polygon area
	 * don't open tooltips by accident; linked shapes click like their marker.
	 */
	bindPolygonEvents() {
		this.polygons.forEach((polygon) => {
			const polygonId = polygon.dataset.polygonId;

			polygon.addEventListener('mouseenter', () => {
				if (!this.isTouchDevice && this.settings.desktopTrigger === 'hover') {
					if (polygonId === this.defaultPolygonId) return;
					if (!this.settings.allowInteraction) return;
					this.cancelDeactivation(polygonId);
					this.activatePolygon(polygonId);
				}
			});

			polygon.addEventListener('mouseleave', (e) => {
				if (!this.isTouchDevice && this.settings.desktopTrigger === 'hover') {
					if (polygonId === this.defaultPolygonId) return;
					if (!this.settings.allowInteraction) return;
					if (this.isRelatedToSamePolygon(e, polygonId)) return;
					this.scheduleDeactivation(polygonId);
				}
			});

			// Without markers the shape is the only control, so it must also open
			// no-link (e.g. sold-out) content.
			if (this.hasPolygonLink(polygonId) || this.settings.displayMode === 'polygons') {
				polygon.addEventListener('click', (e) => {
					e.preventDefault();
					e.stopPropagation();
					if (!this.isPolygonClickable(polygonId)) return;
					this.handlePolygonInteraction(polygonId);
				});
			}
		});
	}

	/**
	 * Bind events to marker elements.
	 */
	bindMarkerEvents() {
		this.markers.forEach((marker) => {
			const polygonId = marker.dataset.polygonId;

			marker.addEventListener('mouseenter', () => {
				if (!this.isTouchDevice && this.settings.desktopTrigger === 'hover') {
					if (polygonId === this.defaultPolygonId) return;
					if (!this.settings.allowInteraction) return;
					this.cancelDeactivation(polygonId);
					this.activatePolygon(polygonId);
				}
			});

			marker.addEventListener('mouseleave', (e) => {
				if (!this.isTouchDevice && this.settings.desktopTrigger === 'hover') {
					if (polygonId === this.defaultPolygonId) return;
					if (!this.settings.allowInteraction) return;
					if (this.isRelatedToSamePolygon(e, polygonId)) return;
					this.scheduleDeactivation(polygonId);
				}
			});

			// Click / tap.
			marker.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				if (!this.isPolygonClickable(polygonId)) return;
				this.handlePolygonInteraction(polygonId);
			});
		});
	}

	/**
	 * Bind tooltip close button events and hover keepalive.
	 */
	bindTooltipCloseEvents() {
		this.tooltips.forEach((tooltip) => {
			const closeBtn = tooltip.querySelector('.wp-block-hotspot-image__tooltip-close');
			if (closeBtn) {
				closeBtn.addEventListener('click', (e) => {
					e.preventDefault();
					e.stopPropagation();
					const polygonId = tooltip.dataset.polygonId;
					this.cancelDeactivation(polygonId);
					// Restore default when closing a non-default polygon.
					// Closing the default tooltip itself intentionally hides it.
					this.deactivateAndRestoreDefault(polygonId);
				});
			}

			const polygonId = tooltip.dataset.polygonId;

			tooltip.addEventListener('mouseenter', () => {
				if (!this.isTouchDevice && this.settings.desktopTrigger === 'hover') {
					this.cancelDeactivation(polygonId);
				}
			});

			tooltip.addEventListener('mouseleave', (e) => {
				if (!this.isTouchDevice && this.settings.desktopTrigger === 'hover') {
					if (this.isRelatedToSamePolygon(e, polygonId)) return;
					this.scheduleDeactivation(polygonId);
				}
			});
		});
	}

	/**
	 * Bind click-outside to dismiss active tooltip.
	 */
	bindOutsideClickEvent() {
		document.addEventListener('click', (e) => {
			// Never dismiss the default polygon — it is always active.
			if (this.activePolygonId && this.activePolygonId !== this.defaultPolygonId && !this.container.contains(e.target)) {
				this.deactivateAndRestoreDefault(this.activePolygonId);
			}
		});

		// Also dismiss when clicking the image background.
		const figure = this.container.querySelector('.wp-block-hotspot-image__figure');
		if (figure) {
			figure.addEventListener('click', (e) => {
				// Only if clicking the figure itself (not a child interactive element).
				if (e.target === figure || e.target.classList.contains('wp-block-hotspot-image__image')) {
					// Never dismiss the default polygon.
					if (this.activePolygonId && this.activePolygonId !== this.defaultPolygonId) {
						this.deactivateAndRestoreDefault(this.activePolygonId);
					}
				}
			});
		}
	}

	/**
	 * Bind keyboard navigation.
	 */
	bindKeyboardEvents() {
		// Handle Enter/Space on focusable marker elements.
		this.container.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				const target = e.target;
				const polygonId = target.dataset?.polygonId;
				if (polygonId && target.matches('.wp-block-hotspot-image__marker')) {
					e.preventDefault();
					if (!this.isPolygonClickable(polygonId)) return;
					this.handlePolygonInteraction(polygonId);
				}
			}

			// Escape dismisses only non-default polygons.
			if (e.key === 'Escape' && this.activePolygonId && this.activePolygonId !== this.defaultPolygonId) {
				this.deactivateAndRestoreDefault(this.activePolygonId);
			}
		});
	}

	/**
	 * Bind resize handler.
	 */
	bindResizeHandler() {
		let resizeTimer;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => {
				if (this.activePolygonId) {
					this.positionTooltip(this.activePolygonId);
				}
			}, 150);
		});
	}

	/**
	 * Handle polygon/marker interaction (click or tap).
	 */
	handlePolygonInteraction(polygonId) {
		// When marker labels are shown, click/tap navigates directly to the link.
		// The default polygon's tooltip is always open, so there is nothing to reveal.
		if (this.settings.showMarkerLabels || polygonId === this.defaultPolygonId) {
			this.followPolygonLink(polygonId);
			return;
		}

		if (this.isTouchDevice) {
			this.handleTouchInteraction(polygonId);
		} else {
			this.handleDesktopInteraction(polygonId);
		}
	}

	/**
	 * Handle desktop click interaction.
	 */
	handleDesktopInteraction(polygonId) {
		if (this.settings.desktopTrigger === 'click') {
			if (this.activePolygonId === polygonId) {
				// Toggle off — restore the default if this isn't the default.
				this.deactivateAndRestoreDefault(polygonId);
			} else {
				// Deactivate previous (no restore — we're immediately activating another).
				if (this.activePolygonId) {
					this.deactivatePolygon(this.activePolygonId);
				}
				this.activatePolygon(polygonId);
			}
		} else {
			// Hover mode: click follows the link.
			this.followPolygonLink(polygonId);
		}
	}

	/**
	 * Handle touch interaction.
	 */
	handleTouchInteraction(polygonId) {
		if (this.settings.mobileTrigger === 'tap_navigate') {
			// Directly follow the link.
			this.followPolygonLink(polygonId);
		} else {
			// tap_reveal: first tap shows content, link inside content navigates.
			if (this.activePolygonId === polygonId) {
				// Already showing — dismiss and restore the default if applicable.
				this.deactivateAndRestoreDefault(polygonId);
			} else {
				if (this.activePolygonId) {
					this.deactivatePolygon(this.activePolygonId);
				}
				this.activatePolygon(polygonId);
			}
		}
	}

	/**
	 * Activate a polygon: show tooltip, apply highlights.
	 */
	activatePolygon(polygonId) {
		// Deactivate the previously active polygon if it's different.
		if (this.activePolygonId && this.activePolygonId !== polygonId) {
			this.cancelDeactivation(this.activePolygonId);
			this.deactivatePolygon(this.activePolygonId);
		}

		this.activePolygonId = polygonId;

		// Add active class to polygon.
		const polygon = this.container.querySelector(`.wp-block-hotspot-image__polygon[data-polygon-id="${polygonId}"]`);
		if (polygon) {
			polygon.classList.add('is-active');
		}

		// Add active class to marker.
		const marker = this.container.querySelector(`.wp-block-hotspot-image__marker[data-polygon-id="${polygonId}"]`);
		if (marker) {
			marker.classList.add('is-active');
		}

		// Show inverse highlight overlay.
		if (this.settings.inverseHighlight) {
			this.showInverseOverlay(polygonId);
		}

		// Show tooltip (skip when marker labels are visible -- title is already shown).
		if (!this.settings.showMarkerLabels) {
			this.showTooltip(polygonId);
		}

		// Add active class to container.
		this.container.classList.add('has-active-polygon');
	}

	/**
	 * Deactivate a polygon: hide tooltip, remove highlights.
	 */
	deactivatePolygon(polygonId) {
		// Clear active tracking if this is the current polygon.
		if (this.activePolygonId === polygonId) {
			this.activePolygonId = null;
		}

		// Remove active class from polygon.
		const polygon = this.container.querySelector(`.wp-block-hotspot-image__polygon[data-polygon-id="${polygonId}"]`);
		if (polygon) {
			polygon.classList.remove('is-active');
		}

		// Remove active class from marker.
		const marker = this.container.querySelector(`.wp-block-hotspot-image__marker[data-polygon-id="${polygonId}"]`);
		if (marker) {
			marker.classList.remove('is-active');
		}

		// Hide inverse highlight overlay.
		if (this.settings.inverseHighlight) {
			this.hideInverseOverlay(polygonId);
		}

		// Hide tooltip.
		this.hideTooltip(polygonId);

		// Remove active class from container.
		this.container.classList.remove('has-active-polygon');
	}

	/**
	 * Show the inverse highlight overlay for a polygon.
	 * This dims everything except the active polygon.
	 */
	showInverseOverlay(polygonId) {
		const overlay = this.container.querySelector(`.wp-block-hotspot-image__inverse-overlay[data-polygon-id="${polygonId}"]`);
		if (overlay) {
			overlay.style.display = 'block';
		}
	}

	/**
	 * Hide the inverse highlight overlay.
	 */
	hideInverseOverlay(polygonId) {
		const overlay = this.container.querySelector(`.wp-block-hotspot-image__inverse-overlay[data-polygon-id="${polygonId}"]`);
		if (overlay) {
			overlay.style.display = 'none';
		}
	}

	/**
	 * Show tooltip for a polygon.
	 */
	showTooltip(polygonId) {
		const tooltip = this.container.querySelector(`.wp-block-hotspot-image__tooltip[data-polygon-id="${polygonId}"]`);
		if (!tooltip) return;

		tooltip.classList.add('is-visible');
		tooltip.setAttribute('aria-hidden', 'false');
		this.tooltipVisible = true;

		// Position the tooltip.
		this.positionTooltip(polygonId);
	}

	/**
	 * Hide tooltip for a polygon.
	 */
	hideTooltip(polygonId) {
		const tooltip = this.container.querySelector(`.wp-block-hotspot-image__tooltip[data-polygon-id="${polygonId}"]`);
		if (!tooltip) return;

		tooltip.classList.remove('is-visible');
		tooltip.setAttribute('aria-hidden', 'true');
		this.tooltipVisible = false;
	}

	/**
	 * Position tooltip to avoid going off-screen.
	 */
	positionTooltip(polygonId) {
		const tooltip = this.container.querySelector(`.wp-block-hotspot-image__tooltip[data-polygon-id="${polygonId}"]`);
		if (!tooltip) return;

		const containerRect = this.container.getBoundingClientRect();
		const tooltipRect = tooltip.getBoundingClientRect();

		// Reset any previous adjustments.
		tooltip.style.transform = '';
		tooltip.classList.remove('is-flipped-x', 'is-flipped-y');

		// Check if tooltip goes off screen to the right.
		if (tooltipRect.right > window.innerWidth) {
			tooltip.classList.add('is-flipped-x');
		}

		// Check if tooltip goes off screen at the bottom.
		if (tooltipRect.bottom > window.innerHeight) {
			tooltip.classList.add('is-flipped-y');
		}

		// Check if tooltip goes off screen to the left.
		if (tooltipRect.left < 0) {
			tooltip.classList.remove('is-flipped-x');
		}
	}

	/**
	 * Follow the link associated with a polygon. Polygons without a link
	 * (e.g. sold out) reveal their content instead, so a tap is never a no-op.
	 */
	followPolygonLink(polygonId) {
		const tooltip = this.container.querySelector(`.wp-block-hotspot-image__tooltip[data-polygon-id="${polygonId}"]`);
		if (!tooltip) return;

		const link = tooltip.querySelector('.wp-block-hotspot-image__tooltip-link');
		if (link && link.href) {
			const target = link.getAttribute('target') || '_self';
			if (target === '_blank') {
				window.open(link.href, '_blank', 'noopener,noreferrer');
			} else {
				window.location.href = link.href;
			}
			return;
		}

		if (polygonId !== this.defaultPolygonId && this.activePolygonId !== polygonId) {
			this.activatePolygon(polygonId);
			// activatePolygon() skips the tooltip when marker labels are on; without
			// a link the tooltip is the only place left for the description.
			this.showTooltip(polygonId);
		}
	}

	/**
	 * Destroy the instance and clean up event listeners.
	 */
	destroy() {
		this.container.classList.remove('has-active-polygon', 'has-default-polygon', 'is-touch-device');
		// Temporarily clear the default so deactivation doesn't auto-restore.
		this.defaultPolygonId = null;
		if (this.activePolygonId) {
			this.deactivatePolygon(this.activePolygonId);
		}
	}
}

/**
 * Initialize all hotspot image blocks on the page.
 */
function initHotspotImages() {
	const blocks = document.querySelectorAll('[data-hotspot-image]');
	blocks.forEach((block) => {
		// Prevent double-initialization.
		if (block._hotspotInstance) return;
		block._hotspotInstance = new HotspotImage(block);
	});
}

// Initialize on DOM ready.
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initHotspotImages);
} else {
	initHotspotImages();
}

// Re-initialize after WordPress navigation (for block themes with client-side routing).
if (typeof wp !== 'undefined' && wp.domReady) {
	wp.domReady(initHotspotImages);
}

export { HotspotImage, initHotspotImages };
